/**
 * Traduzione degli errori API in messaggi comprensibili e azionabili.
 *
 * L'interceptor (api/client.js) espone sull'errore il codice macchina
 * (`err.code`) e i campi strutturati che l'API manda già pronti
 * (`err.data`: limit, current, requested; `err.details`: validazione per
 * campo). Qui li usiamo per dire all'utente COSA è andato storto e cosa può
 * fare, invece di mostrargli un codice grezzo o un generico "riprova".
 */

/**
 * @param {Error} err Errore arricchito dall'interceptor.
 * @param {Function} t Funzione di traduzione.
 * @returns {string|null} Messaggio pronto, o null se il caso non è noto
 *                        (il chiamante decide il fallback).
 */
export const describeApiError = (err, t) => {
  const data = err?.data || {};

  switch (err?.code) {
    case "bots_limit_reached":
      return t("errors.api.botsLimitReached", {
        limit: data.limit,
        current: data.current,
      });
    case "quota_exceeded":
      return t("errors.api.sourcesQuotaExceeded", {
        limit: data.limit,
        current: data.current,
        requested: data.requested,
      });
    case "sources_limit_reached":
      return t("errors.api.sourcesLimitReached", { limit: data.limit });
    case "domains_limit_reached":
      return t("errors.api.domainsLimitReached", { limit: data.limit });
    case "organizations_limit_reached":
      return t("errors.api.organizationsLimitReached", { limit: data.limit });
    case "User already exists":
      return t("errors.api.emailAlreadyExists");
    case "Configuration error":
      return t("errors.api.configuration");
    case "network_error":
      return t("errors.api.network");
    case "Validation failed": {
      // express-validator: details[] = [{ field, msg }]
      const details = Array.isArray(err?.details) ? err.details : [];
      const messages = details
        .map((d) => d?.msg || d?.message)
        .filter(Boolean);
      return messages.length ? messages.join(" ") : t("errors.api.validation");
    }
    default:
      break;
  }

  if (err?.status === 429) return t("errors.api.tooManyRequests");
  if (err?.status === 403) return t("errors.api.forbidden");
  if (err?.status >= 500) return t("errors.api.server");

  return null;
};

/**
 * L'analisi del sito può fallire per cause molto diverse fra loro, e il
 * backend le distingue (websiteAnalyzerV2), ma le restituisce tutte come 500:
 * il motivo vero arriva quindi in `err.data.message`. Senza questa mappa
 * diremmo "il sito non esiste" anche quando a fallire è il nostro motore AI —
 * accusando ingiustamente il sito dell'utente.
 *
 * La pagina di errore del design (frame 4060:1206) mostra il box rosso con
 * un titolo in grassetto e una spiegazione: per questo la funzione ritorna
 * { title, description } per ogni causa.
 *
 * @param {Error} err Errore arricchito dall'interceptor.
 * @param {Function} t Funzione di traduzione.
 * @returns {{title: string, description: string}} Causa del fallimento.
 */
export const describeAnalysisError = (err, t) => {
  const cause = (key, params) => ({
    title: t(`onboardingWizard.${key}Title`),
    description: t(`onboardingWizard.${key}`, params),
  });

  if (err?.code === "network_error") {
    return {
      title: t("onboardingWizard.analysisFailedGenericTitle"),
      description: t("errors.api.network"),
    };
  }

  const raw = err?.data?.message || err?.message || "";

  if (/Invalid URL format/i.test(raw)) {
    return cause("analysisFailedUrl");
  }

  // "Could not access website: <messaggio axios>" — dentro c'è il dettaglio
  // che serve davvero all'utente: lo stato HTTP o il tipo di errore di rete.
  if (/Could not access website/i.test(raw)) {
    const status = raw.match(/status code (\d{3})/)?.[1];
    if (status) {
      if (status === "403" || status === "401") {
        return cause("analysisFailedBlocked", { status });
      }
      if (status === "404") {
        return cause("analysisFailedNotFound", { status });
      }
      return cause("analysisFailedSiteError", { status });
    }
    if (/timeout/i.test(raw)) {
      return cause("analysisFailedTimeout");
    }
    if (/ENOTFOUND|EAI_AGAIN|getaddrinfo/i.test(raw)) {
      return cause("analysisFailedDns");
    }
    if (/ECONNREFUSED|ECONNRESET|EHOSTUNREACH/i.test(raw)) {
      return cause("analysisFailedRefused");
    }
    if (/certificate|CERT_|SSL|TLS/i.test(raw)) {
      return cause("analysisFailedTls");
    }
    return cause("analysisFailedUnreachable");
  }

  if (/Invalid HTML structure/i.test(raw)) {
    return cause("analysisFailedUnreadable");
  }

  // "AI analysis failed: <messaggio OpenAI>"
  if (/AI analysis failed/i.test(raw)) {
    if (/rate limit|429|quota/i.test(raw)) {
      return cause("analysisFailedAiRateLimit");
    }
    if (/context length|maximum context|too many tokens/i.test(raw)) {
      return cause("analysisFailedAiTooBig");
    }
    return cause("analysisFailedAi");
  }

  return cause("analysisFailedGeneric");
};
